import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Empty, ErrorMsg, Loading, PageHead, Section } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import type { Asset, Project } from '../../lib/contentTypes';
import { AssetCard, uploadAssets } from './common';

/** What the server records on an image made here (app/services/reframe.py). */
export interface Derivation {
  kind: 'reframe';
  source_filename: string;
  ratio: string;
  crop: { left: number; top: number; width: number; height: number };
  source_size: [number, number];
  max_edge: number | null;
}
export type ReframedAsset = Asset & { derived_from_id: string | null; derivation: Derivation | null };

interface Ratio {
  w: number;
  h: number;
}
/** Left, top and width of the frame as fractions of the (EXIF-rotated) image; the height follows from the ratio. */
interface Frame {
  x: number;
  y: number;
  w: number;
}
interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}
interface Made {
  source_id: string;
  created: boolean;
  crop: Box;
  asset: ReframedAsset;
}

const PRESETS: Ratio[] = [
  { w: 4, h: 5 },
  { w: 1, h: 1 },
  { w: 9, h: 16 },
  { w: 16, h: 9 },
  { w: 3, h: 2 },
  { w: 2, h: 3 },
];
const SIZE_LIMITS: { label: string; value: number | null }[] = [
  { label: 'Full resolution', value: null },
  { label: 'Longest side 4096 px', value: 4096 },
  { label: 'Longest side 2048 px', value: 2048 },
  { label: 'Longest side 1600 px', value: 1600 },
  { label: 'Longest side 1080 px', value: 1080 },
];
const MAX_TERM = 100;
const MAX_SKEW = 10;
const SOFT_BELOW = 1080; // narrower than a phone screen's width in pixels: it will look soft

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
const reduce = (r: Ratio): Ratio => {
  const g = gcd(r.w, r.h);
  return { w: r.w / g, h: r.h / g };
};
const same = (a: Ratio, b: Ratio) => a.w === b.w && a.h === b.h;
const label = (r: Ratio) => `${r.w}:${r.h}`;
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/** The server's rule for a ratio typed by hand (app/services/reframe.py parse_ratio). */
function ratioProblem(w: number, h: number): string | null {
  if (!Number.isInteger(w) || !Number.isInteger(h) || w < 1 || h < 1 || w > MAX_TERM || h > MAX_TERM) {
    return `A ratio is two whole numbers from 1 to ${MAX_TERM}.`;
  }
  const r = reduce({ w, h });
  if (r.w > MAX_SKEW * r.h || r.h > MAX_SKEW * r.w) return `${label(r)} is a strip: keep it between 1:${MAX_SKEW} and ${MAX_SKEW}:1.`;
  return null;
}

/** How many whole ratio steps fit in the image (the largest frame is kMax*w by kMax*h pixels). */
const kMaxOf = (a: Asset, r: Ratio) => Math.min(Math.floor(a.width / r.w), Math.floor(a.height / r.h));

/** The server's pixel box for a frame (frame_box in reframe.py): width and height are k*ratio.w and
 *  k*ratio.h, so the ratio is exact. */
function boxOf(a: Asset, r: Ratio, f: Frame | null): Box {
  const kMax = Math.max(1, kMaxOf(a, r));
  const k = f ? clamp(Math.round((f.w * a.width) / r.w), 1, kMax) : kMax;
  const width = k * r.w;
  const height = k * r.h;
  if (!f) return { left: Math.floor((a.width - width) / 2), top: Math.floor((a.height - height) / 2), width, height };
  return {
    left: clamp(Math.round(f.x * a.width), 0, Math.max(0, a.width - width)),
    top: clamp(Math.round(f.y * a.height), 0, Math.max(0, a.height - height)),
    width,
    height,
  };
}
const frameOf = (a: Asset, b: Box): Frame => ({ x: b.left / a.width, y: b.top / a.height, w: b.width / a.width });
/** Snap a raw frame to the pixel grid the server will cut, so what is drawn is what is saved. */
const snap = (a: Asset, r: Ratio, f: Frame): Frame => frameOf(a, boxOf(a, r, f));
const largest = (a: Asset, r: Ratio): Frame => frameOf(a, boxOf(a, r, null));
/** The size the file will have once the size limit is applied (the server keeps the ratio exact). */
function outputSize(b: Box, r: Ratio, maxEdge: number | null): { width: number; height: number } {
  if (maxEdge && Math.max(b.width, b.height) > maxEdge) {
    const k = Math.max(1, Math.floor(maxEdge / Math.max(r.w, r.h)));
    if (k * r.w < b.width) return { width: k * r.w, height: k * r.h };
  }
  return { width: b.width, height: b.height };
}

function RatioPicker({ value, onChange, compact }: { value: Ratio; onChange: (r: Ratio) => void; compact?: boolean }) {
  const preset = PRESETS.find((p) => same(p, value));
  const [custom, setCustom] = useState(!preset);
  const [w, setW] = useState(String(value.w));
  const [h, setH] = useState(String(value.h));
  const problem = custom ? ratioProblem(Number(w), Number(h)) : null;
  function typed(nw: string, nh: string) {
    setW(nw);
    setH(nh);
    if (!ratioProblem(Number(nw), Number(nh))) onChange(reduce({ w: Number(nw), h: Number(nh) }));
  }
  const customFields = custom && (
    <span className="rf-custom">
      <input type="number" min={1} max={MAX_TERM} value={w} onChange={(e) => typed(e.target.value, h)} aria-label="Ratio width" />
      <span aria-hidden="true">:</span>
      <input type="number" min={1} max={MAX_TERM} value={h} onChange={(e) => typed(w, e.target.value)} aria-label="Ratio height" />
      {problem && <span className="err-text small">{problem}</span>}
    </span>
  );
  if (compact) {
    return (
      <span className="rf-ratio-select">
        <select
          aria-label="Ratio of this image"
          value={custom ? 'custom' : label(value)}
          onChange={(e) => {
            if (e.target.value === 'custom') {
              setCustom(true);
              return;
            }
            setCustom(false);
            const p = PRESETS.find((x) => label(x) === e.target.value);
            if (p) onChange(p);
          }}
        >
          {PRESETS.map((p) => (
            <option key={label(p)} value={label(p)}>
              {label(p)}
            </option>
          ))}
          <option value="custom">Other…</option>
        </select>
        {customFields}
      </span>
    );
  }
  return (
    <span className="rf-ratio" role="group" aria-label="Ratio">
      {PRESETS.map((p) => (
        <button
          key={label(p)}
          type="button"
          className={`btn small${!custom && preset && same(p, value) ? ' primary' : ''}`}
          aria-pressed={!custom && !!preset && same(p, value)}
          onClick={() => {
            setCustom(false);
            setW(String(p.w));
            setH(String(p.h));
            onChange(p);
          }}
        >
          {label(p)}
        </button>
      ))}
      <button type="button" className={`btn small${custom ? ' primary' : ''}`} aria-pressed={custom} onClick={() => setCustom(true)}>
        Other
      </button>
      {customFields}
    </span>
  );
}

type Corner = 'nw' | 'ne' | 'sw' | 'se';
interface Drag {
  mode: 'move' | 'resize';
  px: number;
  py: number;
  box: Box;
  corner?: Corner;
}

/** One image with its frame: drag the frame to move it, drag a corner to resize it (the ratio stays),
 *  or use the arrow keys, + / - and the size slider. */
function FrameEditor({ asset, ratio, frame, maxEdge, onChange }: { asset: Asset; ratio: Ratio; frame: Frame; maxEdge: number | null; onChange: (f: Frame) => void }) {
  const stage = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const W = asset.width;
  const H = asset.height;
  const kMax = kMaxOf(asset, ratio);
  const box = boxOf(asset, ratio, frame);
  const out = outputSize(box, ratio, maxEdge);
  const wMax = (Math.max(1, kMax) * ratio.w) / W;
  const unitW = ratio.w / W;
  /** Width fraction per unit of height fraction, for this image and ratio. */
  const kk = (H * ratio.w) / (ratio.h * W);

  const set = (f: Frame) => onChange(snap(asset, ratio, f));
  const resizeAround = (w: number) => {
    const nw = clamp(w, Math.max(unitW, 0.04), wMax);
    const cx = box.left / W + box.width / W / 2;
    const cy = box.top / H + box.height / H / 2;
    const hf = nw / kk;
    set({ x: clamp(cx - nw / 2, 0, 1 - nw), y: clamp(cy - hf / 2, 0, 1 - hf), w: nw });
  };
  const at = (e: PointerEvent) => {
    const r = stage.current!.getBoundingClientRect();
    return { fx: (e.clientX - r.left) / r.width, fy: (e.clientY - r.top) / r.height };
  };

  function down(e: PointerEvent<HTMLElement>, mode: Drag['mode'], corner?: Corner) {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const { fx, fy } = at(e);
    drag.current = { mode, px: fx, py: fy, box, corner };
    if (mode === 'move') e.currentTarget.focus();
  }
  function move(e: PointerEvent<HTMLElement>) {
    const d = drag.current;
    if (!d) return;
    const { fx, fy } = at(e);
    if (d.mode === 'move') {
      const wf = d.box.width / W;
      const hf = d.box.height / H;
      set({ x: clamp(d.box.left / W + (fx - d.px), 0, 1 - wf), y: clamp(d.box.top / H + (fy - d.py), 0, 1 - hf), w: wf });
      return;
    }
    // resize: the corner opposite the dragged one stays where it is
    const east = d.corner!.endsWith('e');
    const south = d.corner!.startsWith('s');
    const ax = (east ? d.box.left : d.box.left + d.box.width) / W;
    const ay = (south ? d.box.top : d.box.top + d.box.height) / H;
    const availX = east ? 1 - ax : ax;
    const availY = south ? 1 - ay : ay;
    const raw = Math.max(Math.abs(fx - ax), Math.abs(fy - ay) * kk);
    const w0 = clamp(raw, Math.max(unitW, 0.04), Math.max(unitW, Math.min(availX, availY * kk, wMax)));
    // snap the width first, so the corner that stays put really stays (a rounded left edge would move it)
    const w = snap(asset, ratio, { x: 0, y: 0, w: w0 }).w;
    const hf = w / kk;
    set({ x: east ? ax : ax - w, y: south ? ay : ay - hf, w });
  }
  const up = (e: PointerEvent<HTMLElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    drag.current = null;
  };
  function key(e: KeyboardEvent<HTMLDivElement>) {
    const step = e.shiftKey ? 0.05 : 0.01;
    const wf = box.width / W;
    const hf = box.height / H;
    const x = box.left / W;
    const y = box.top / H;
    const moves: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (moves[e.key]) {
      const [dx, dy] = moves[e.key];
      set({ x: clamp(x + dx, 0, 1 - wf), y: clamp(y + dy, 0, 1 - hf), w: wf });
    } else if (e.key === '+' || e.key === '=') resizeAround(wf * 1.05);
    else if (e.key === '-' || e.key === '_') resizeAround(wf / 1.05);
    else return;
    e.preventDefault();
  }

  const ar = W / H;
  const soft = out.width < SOFT_BELOW;
  return (
    <div className="rf-editor">
      <div className="rf-stage" ref={stage} style={{ aspectRatio: `${W} / ${H}`, width: `min(100%, ${(62 * ar).toFixed(2)}vh)` }}>
        <img src={asset.urls.work} alt="" draggable={false} />
        <div
          className="rf-frame"
          role="group"
          tabIndex={0}
          aria-label={`Frame for ${asset.filename}, ratio ${label(ratio)}. Arrow keys move it, plus and minus resize it.`}
          data-x={frame.x.toFixed(5)}
          data-y={frame.y.toFixed(5)}
          data-w={frame.w.toFixed(5)}
          data-box={`${box.left},${box.top},${box.width},${box.height}`}
          style={{ left: `${(box.left / W) * 100}%`, top: `${(box.top / H) * 100}%`, width: `${(box.width / W) * 100}%`, height: `${(box.height / H) * 100}%` }}
          onPointerDown={(e) => down(e, 'move')}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          onKeyDown={key}
        >
          {(['nw', 'ne', 'sw', 'se'] as Corner[]).map((c) => (
            <span key={c} className={`rf-handle ${c}`} aria-hidden="true" onPointerDown={(e) => down(e, 'resize', c)} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
          ))}
        </div>
      </div>
      <div className="rf-controls">
        <label className="field rf-size">
          <span>Frame size</span>
          <input
            type="range"
            min={4}
            max={100}
            value={Math.round(((box.width / W) / wMax) * 100)}
            onChange={(e) => resizeAround((Number(e.target.value) / 100) * wMax)}
            aria-label={`Frame size for ${asset.filename}`}
          />
        </label>
        <button type="button" className="btn small" onClick={() => onChange(largest(asset, ratio))}>
          Largest frame
        </button>
        <p className="small num rf-readout" data-testid="rf-readout">
          Result {out.width} × {out.height} px
          {out.width !== box.width && <span className="muted"> (the frame is {box.width} × {box.height}; cut down to the size limit)</span>}
          {soft && <span className="warn-text"> · under {SOFT_BELOW} px wide: it will look soft on a phone</span>}
        </p>
      </div>
    </div>
  );
}

export function Reframe() {
  const [sp] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState('');
  const [assets, setAssets] = useState<ReframedAsset[] | null>(null);
  const known = useRef(new Map<string, ReframedAsset>());
  const [selected, setSelected] = useState<string[]>([]);
  const [ratio, setRatio] = useState<Ratio>(PRESETS[0]);
  const [sameRatio, setSameRatio] = useState(true);
  const [ratios, setRatios] = useState<Record<string, Ratio>>({});
  const [frames, setFrames] = useState<Record<string, Frame>>({});
  const [maxEdge, setMaxEdge] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [made, setMade] = useState<Made[] | null>(null);
  const preselected = useRef(false);

  const ratioOf = useCallback((id: string) => (sameRatio ? ratio : (ratios[id] ?? ratio)), [sameRatio, ratio, ratios]);

  const load = useCallback(() => {
    const q = new URLSearchParams({ kind: 'image' });
    if (projectId) q.set('project_id', projectId);
    return api<{ items: ReframedAsset[] }>(`/api/content/assets?${q}`)
      .then((d) => {
        for (const a of d.items) known.current.set(a.id, a);
        setAssets(d.items);
      })
      .catch((e) => setError(errorText(e)));
  }, [projectId]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    api<{ items: Project[] }>('/api/content/projects')
      .then((d) => setProjects(d.items))
      .catch(() => setProjects([]));
  }, []);

  const select = useCallback(
    (ids: string[]) => {
      setSelected((cur) => [...cur, ...ids.filter((i) => !cur.includes(i) && known.current.has(i))]);
      setFrames((cur) => {
        const next = { ...cur };
        for (const id of ids) {
          const a = known.current.get(id);
          if (a && !next[id] && kMaxOf(a, ratioOf(id)) >= 1) next[id] = largest(a, ratioOf(id));
        }
        return next;
      });
    },
    [ratioOf],
  );
  // /content/prepare?asset=ID&asset=ID (the Assets tab's "Reframe" link) starts with those images chosen
  useEffect(() => {
    if (!assets || preselected.current) return;
    preselected.current = true;
    const ids = sp.getAll('asset');
    if (ids.length) select(ids);
  }, [assets, sp, select]);

  function toggle(a: ReframedAsset) {
    if (selected.includes(a.id)) {
      setSelected(selected.filter((i) => i !== a.id));
      return;
    }
    select([a.id]);
  }
  function replace(next: Ratio, nextSame: boolean, nextRatios: Record<string, Ratio>) {
    // a new ratio places every frame again: the largest frame, centred
    setFrames(() => {
      const f: Record<string, Frame> = {};
      for (const id of selected) {
        const a = known.current.get(id);
        const r = nextSame ? next : (nextRatios[id] ?? next);
        if (a && kMaxOf(a, r) >= 1) f[id] = largest(a, r);
      }
      return f;
    });
  }
  function changeRatio(r: Ratio) {
    setRatio(r);
    replace(r, sameRatio, ratios);
  }
  function changeSame(v: boolean) {
    setSameRatio(v);
    replace(ratio, v, ratios);
  }
  function changeOwnRatio(id: string, r: Ratio) {
    const next = { ...ratios, [id]: r };
    setRatios(next);
    const a = known.current.get(id);
    if (a && kMaxOf(a, r) >= 1) setFrames((cur) => ({ ...cur, [id]: largest(a, r) }));
  }

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    setError('');
    const { done, errors } = await uploadAssets(Array.from(files), { projectId: projectId || null, onProgress: setMsg });
    setMsg(`${done.length} uploaded${errors.length ? `; refused: ${errors.join('; ')}` : ''}`);
    for (const a of done) known.current.set(a.id, a as ReframedAsset);
    await load();
    select(done.map((a) => a.id));
  }

  const chosen = selected.map((id) => known.current.get(id)).filter((a): a is ReframedAsset => !!a);
  const tooSmall = chosen.filter((a) => kMaxOf(a, ratioOf(a.id)) < 1);
  const ready = chosen.length > 0 && tooSmall.length === 0 && !busy;

  async function save() {
    setBusy(true);
    setError('');
    setMsg('');
    setMade(null);
    try {
      const body = {
        ratio,
        max_edge: maxEdge,
        items: chosen.map((a) => ({
          asset_id: a.id,
          ...(sameRatio ? {} : { ratio: ratioOf(a.id) }),
          frame: frames[a.id],
        })),
      };
      const res = await api<{ items: Made[] }>('/api/reframe', { method: 'POST', body });
      setMade(res.items);
      for (const m of res.items) known.current.set(m.asset.id, m.asset);
      const fresh = res.items.filter((m) => m.created).length;
      setMsg(`${res.items.length} image${res.items.length === 1 ? '' : 's'} ready: ${fresh} new${res.items.length - fresh ? `, ${res.items.length - fresh} already made earlier` : ''}.`);
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <PageHead title="Prepare images" meta="Reframe one or several images to a ratio. Each result is a NEW image cut from the original at full resolution; the original is never changed.">
        <Link className="btn" to="/content?tab=assets">
          Assets
        </Link>
      </PageHead>

      <Section
        title="1 · Choose images"
        aside={
          <label className="btn file-btn">
            Upload images
            <input type="file" accept="image/jpeg,image/png" multiple onChange={(e) => void onFiles(e.target.files)} aria-label="Upload images" />
          </label>
        }
      >
        <div className="row gap wrap center cs-filter">
          <label className="field cs-inline">
            <span>Project</span>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label="Filter by project">
              <option value="">all images</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </label>
          {assets && assets.length > 0 && (
            <>
              <button type="button" className="btn small" onClick={() => select(assets.map((a) => a.id))}>
                Select all shown
              </button>
              <button type="button" className="btn small" disabled={selected.length === 0} onClick={() => setSelected([])}>
                Clear selection
              </button>
            </>
          )}
          <span className="muted small" aria-live="polite">
            {selected.length} selected
          </span>
        </div>
        {!assets ? (
          <Loading what="Loading images" />
        ) : assets.length === 0 ? (
          <Empty>No images{projectId ? ' in this project' : ''} yet. Upload some above.</Empty>
        ) : (
          <div className="cs-assets small-grid">
            {assets.map((a) => {
              const i = selected.indexOf(a.id);
              return (
                <AssetCard key={a.id} asset={a} selected={i >= 0 ? i : null} onSelect={() => toggle(a)} />
              );
            })}
          </div>
        )}
      </Section>

      {chosen.length > 0 && (
        <Section title="2 · Ratio and size">
          <div className="rf-options">
            <div className="rf-option">
              <span className="rf-option-label">Ratio</span>
              <RatioPicker value={ratio} onChange={changeRatio} />
            </div>
            <label className="rf-same">
              <input type="checkbox" checked={sameRatio} onChange={(e) => changeSame(e.target.checked)} /> Same ratio for all
            </label>
            <label className="field cs-inline">
              <span>Size</span>
              <select value={maxEdge ?? ''} onChange={(e) => setMaxEdge(e.target.value ? Number(e.target.value) : null)} aria-label="Size limit">
                {SIZE_LIMITS.map((s) => (
                  <option key={s.label} value={s.value ?? ''}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="muted small">Changing the ratio places the frames again, as large as possible and centred.</p>
        </Section>
      )}

      {chosen.length > 0 && (
        <Section
          title="3 · Place the frames"
          aside={
            <button type="button" className="btn primary" disabled={!ready} onClick={() => void save()} aria-busy={busy}>
              {busy ? 'Working…' : `Reframe ${chosen.length} image${chosen.length === 1 ? '' : 's'}`}
            </button>
          }
        >
          <p className="muted small">Drag the frame to move it, drag a corner to resize it. Keyboard: arrow keys move it (Shift for bigger steps), + and − resize it.</p>
          <div className="rf-cards">
            {chosen.map((a) => {
              const r = ratioOf(a.id);
              const small = kMaxOf(a, r) < 1;
              return (
                <article key={a.id} className="rf-card" data-asset={a.id} data-filename={a.filename}>
                  <header className="rf-card-head">
                    <strong className="rf-card-name" title={a.filename}>
                      {a.filename}
                    </strong>
                    <span className="muted small num">
                      {a.width}×{a.height}
                    </span>
                    {!sameRatio && <RatioPicker value={r} onChange={(nr) => changeOwnRatio(a.id, nr)} compact />}
                    <button type="button" className="btn small" onClick={() => setSelected(selected.filter((i) => i !== a.id))} aria-label={`Remove ${a.filename}`}>
                      Remove
                    </button>
                  </header>
                  {small || !frames[a.id] ? (
                    <p className="msg err">
                      This image is {a.width}×{a.height}: too small for {label(r)}.
                    </p>
                  ) : (
                    <FrameEditor asset={a} ratio={r} frame={frames[a.id]} maxEdge={maxEdge} onChange={(f) => setFrames((cur) => ({ ...cur, [a.id]: f }))} />
                  )}
                </article>
              );
            })}
          </div>
          {tooSmall.length > 0 && <p className="msg err">Remove the image{tooSmall.length === 1 ? '' : 's'} that {tooSmall.length === 1 ? 'is' : 'are'} too small, or pick another ratio.</p>}
        </Section>
      )}

      <ErrorMsg>{error}</ErrorMsg>
      {msg && (
        <p className="msg info" role="status">
          {msg}
        </p>
      )}

      {made && (
        <Section
          title="Your new images"
          aside={
            made.length > 1 ? (
              <a className="btn" href={`/api/reframe/zip?${made.map((m) => `ids=${m.asset.id}`).join('&')}`} download="prepared-images.zip">
                Download all ({made.length}) as a zip
              </a>
            ) : undefined
          }
        >
          <div className="cs-assets small-grid" data-testid="rf-results">
            {made.map((m) => (
              <AssetCard key={m.asset.id} asset={m.asset}>
                <span className="small muted">{m.created ? 'new' : 'already made earlier'}</span>
                <a className="btn small" href={m.asset.urls.original} download={m.asset.filename}>
                  Download
                </a>
              </AssetCard>
            ))}
          </div>
          <p className="muted small">
            They are in <Link to="/content?tab=assets">Assets</Link>, in the same project and with the same usage status as the originals.
          </p>
        </Section>
      )}
    </div>
  );
}
